import { aucklandLocalToUtc } from "../../../shared/time";

export interface AucklandDate {
  year: number;
  month: number;
  day: number;
}

export function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function dateKey(parts: AucklandDate): string {
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
}

export function aucklandParts(date: Date): AucklandDate {
  const formatted = new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const read = (type: string) => Number(formatted.find((part) => part.type === type)?.value ?? 0);
  return { year: read("year"), month: read("month"), day: read("day") };
}

export function todayKey(): string {
  return dateKey(aucklandParts(new Date()));
}

export function monthStartKey(key = todayKey()): string {
  return `${key.slice(0, 7)}-01`;
}

export function addDays(key: string, days: number): string {
  const iso = aucklandLocalToUtc(`${key}T12:00`);
  return dateKey(aucklandParts(new Date(new Date(iso).getTime() + days * 86_400_000)));
}

function weekdayMonday(key: string): number {
  const iso = aucklandLocalToUtc(`${key}T12:00`);
  const weekday = new Intl.DateTimeFormat("en-US", { timeZone: "Pacific/Auckland", weekday: "long" }).format(new Date(iso));
  const map: Record<string, number> = { Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6 };
  return map[weekday] ?? 0;
}

export function monthGrid(year: number, month: number): string[] {
  const first = `${year}-${pad(month)}-01`;
  const start = addDays(first, -weekdayMonday(first));
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

export function weekKeys(anchor: string): string[] {
  const start = addDays(anchor, -weekdayMonday(anchor));
  return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}

export function shiftMonthKey(key: string, delta: number): string {
  const index = Number(key.slice(0, 4)) * 12 + (Number(key.slice(5, 7)) - 1) + delta;
  const year = Math.floor(index / 12);
  const month = index - year * 12 + 1;
  return `${year}-${pad(month)}-01`;
}

export function rangeFor(startKey: string, endKeyInclusive: string): { from: string; to: string } {
  return {
    from: aucklandLocalToUtc(`${startKey}T00:00`),
    to: aucklandLocalToUtc(`${addDays(endKeyInclusive, 1)}T00:00`),
  };
}

export function overlapsDay(startsAt: string, endsAt: string, day: string): boolean {
  const bounds = rangeFor(day, day);
  return startsAt < bounds.to && endsAt > bounds.from;
}

export function dayLabel(key: string): string {
  return new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(aucklandLocalToUtc(`${key}T12:00`)));
}

export function dayNumber(key: string): string {
  return String(Number(key.slice(-2)));
}

export function monthTitle(key: string): string {
  return new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    month: "long",
    year: "numeric",
  }).format(new Date(aucklandLocalToUtc(`${key.slice(0, 7)}-01T12:00`)));
}

export function formatDateKey(key: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return key;
  return new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(aucklandLocalToUtc(`${key}T12:00`)));
}
